import { useState, useMemo } from 'react'
import { getErrorDescription } from '@/lib/error-utils'
import { reportError } from '@/lib/error-reporting'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Empty, EmptyMedia, EmptyDescription } from '@/components/ui/empty'
import {
  Table as TableUI,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Calendar, Plus, Trash2, AlertTriangle, Loader2 } from 'lucide-react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import type { InquiryEvent, InquiryLite, EventLite } from '@/gen/cortado/v1/inquiries_pb'
import type { QuoteLite } from '@/gen/cortado/v1/invoices_pb'
import { QuoteStatusEnum } from '@/gen/cortado/v1/settings_pb'
import { Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import { DataTableColumnHeader } from '@/components/shared/data-table/column-header'
import { formatDateTime } from '@/lib/date-utils'
import { formatTimestampToTimeString } from '@/lib/inquiry-utils'
import { EventTypeLabel } from '@/components/shared/enum-badge'
import { useDeleteInquiryEvent } from '@/stores/leads.store'
import { toast } from 'sonner'
import { InquiryEventEditSheet } from './inquiry-event-edit-sheet'
import { InquiryEventCreateSheet } from './inquiry-event-create-sheet'

// 2-hour buffer in milliseconds (matches backend logic)
const BUFFER_MS = 2 * 60 * 60 * 1000

export interface ConflictInfo {
  hasConflict: boolean
  conflictingInquiries: InquiryLite[]
  conflictingEvents: EventLite[]
}

/**
 * Checks if an event conflicts with overlapping inquiries/events using a 2-hour buffer
 */
export function getEventConflicts(
  event: InquiryEvent,
  overlappingInquiries: InquiryLite[],
  overlappingEvents: EventLite[]
): ConflictInfo {
  if (!event.eventDate) {
    return { hasConflict: false, conflictingInquiries: [], conflictingEvents: [] }
  }

  const eventSeconds = typeof event.eventDate.seconds === 'bigint'
    ? Number(event.eventDate.seconds)
    : event.eventDate.seconds
  const eventStart = eventSeconds * 1000
  const eventDurationMs = (event.eventLength || 0) * 60 * 60 * 1000
  const eventEnd = eventStart + eventDurationMs

  // Event window with buffer
  const windowStart = eventStart - BUFFER_MS
  const windowEnd = eventEnd + BUFFER_MS

  const conflictingInquiries = overlappingInquiries.filter((inquiry) => {
    if (!inquiry.firstEventDate) return false
    const inquirySeconds = typeof inquiry.firstEventDate.seconds === 'bigint'
      ? Number(inquiry.firstEventDate.seconds)
      : inquiry.firstEventDate.seconds
    const inquiryTime = inquirySeconds * 1000
    // Inquiry conflicts if it falls within our window
    return inquiryTime >= windowStart && inquiryTime <= windowEnd
  })

  const conflictingEvents = overlappingEvents.filter((evt) => {
    if (!evt.eventDate) return false
    const evtSeconds = typeof evt.eventDate.seconds === 'bigint'
      ? Number(evt.eventDate.seconds)
      : evt.eventDate.seconds
    const evtStart = evtSeconds * 1000
    const evtDurationMs = (evt.eventLength || 0) * 60 * 60 * 1000
    const evtEnd = evtStart + evtDurationMs
    // Events conflict if their windows overlap
    const evtWindowStart = evtStart - BUFFER_MS
    const evtWindowEnd = evtEnd + BUFFER_MS
    return windowStart < evtWindowEnd && windowEnd > evtWindowStart
  })

  return {
    hasConflict: conflictingInquiries.length > 0 || conflictingEvents.length > 0,
    conflictingInquiries,
    conflictingEvents,
  }
}

interface LeadEventsCardProps {
  inquiryId: string
  events: InquiryEvent[]
  /** Inquiry-level event type for defaulting new events */
  inquiryEventType?: string
  /** Inquiry-level event location for defaulting new events */
  inquiryEventLocation?: string
  /** Overlapping inquiries for conflict detection */
  overlappingInquiries?: InquiryLite[]
  /** Overlapping events for conflict detection */
  overlappingEvents?: EventLite[]
  /** This lead's quotes, used to warn that a quote's copy of these events is now independent */
  quotes?: QuoteLite[]
}

// Statuses that mean a quote is still live and outstanding with the client — sent but not yet
// accepted, and still capable of being accepted (unlike rejected/expired/cancelled quotes, which
// are dead and excluded before bucket selection below).
const LIVE_QUOTE_STATUSES = new Set([
  QuoteStatusEnum.QUOTE_STATUS_SENT,
  QuoteStatusEnum.QUOTE_STATUS_VIEWED,
  QuoteStatusEnum.QUOTE_STATUS_REVISED,
])

function quoteCreatedAtMs(quote: QuoteLite): number {
  if (!quote.createdAt) return 0
  const seconds = typeof quote.createdAt.seconds === 'bigint'
    ? Number(quote.createdAt.seconds)
    : quote.createdAt.seconds
  return seconds * 1000
}

/**
 * Warns that this lead's events are no longer the source of truth once a quote has copied them.
 * Picks the most-advanced quote status present: accepted > outstanding (sent/viewed/etc.) > draft.
 */
export function QuoteEventsAdvisory({ quotes }: { quotes: QuoteLite[] }) {
  if (quotes.length === 0) return null

  if (quotes.some((q) => q.status === QuoteStatusEnum.QUOTE_STATUS_ACCEPTED)) {
    return (
      <div className="flex items-start gap-3 rounded-lg border border-status-warning-border bg-status-warning-bg/60 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning-fg" />
        <p className="text-sm text-status-warning-fg">
          These events are already booked. Changes here won't update them — edit the event itself instead.
        </p>
      </div>
    )
  }

  // Rejected/expired/cancelled quotes are dead — they'll never be accepted, so there's nothing to
  // keep in sync. Drop them (and any unknown status) before picking a bucket, so a dead quote can't
  // shadow a still-live quote that actually needs attention.
  const liveQuotes = quotes.filter((q) =>
    q.status === QuoteStatusEnum.QUOTE_STATUS_DRAFT || LIVE_QUOTE_STATUSES.has(q.status)
  )
  if (liveQuotes.length === 0) return null

  const isOutstanding = liveQuotes.some((q) => LIVE_QUOTE_STATUSES.has(q.status))
  const matches = liveQuotes
    .filter((q) => isOutstanding
      ? LIVE_QUOTE_STATUSES.has(q.status)
      : q.status === QuoteStatusEnum.QUOTE_STATUS_DRAFT)
    .sort((a, b) => quoteCreatedAtMs(b) - quoteCreatedAtMs(a))

  if (matches.length === 0) return null

  const primary = matches[0]
  const otherCount = matches.length - 1
  const isPlural = otherCount > 0

  const quoteRef = (
    <Link
      to="/quotes/$id"
      params={{ id: primary.id }}
      className="font-medium underline underline-offset-2 hover:opacity-80"
    >
      Quote #{primary.quoteNumber}
    </Link>
  )

  return (
    <div className="flex items-start gap-3 rounded-lg border border-status-warning-border bg-status-warning-bg/60 px-4 py-3">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-warning-fg" />
      <p className="text-sm text-status-warning-fg">
        {quoteRef}
        {isPlural && ` and ${otherCount} other quote${otherCount > 1 ? 's' : ''}`}
        {isOutstanding ? (
          isPlural
            ? " have their own copies of these events, and that's what the client sees and what becomes the real events on acceptance. Editing here won't update them — send revisions instead."
            : ' has its own copy of these events, and that copy is what the client sees and what becomes the real events on acceptance. Editing here won\'t update it — send a revision instead.'
        ) : (
          isPlural
            ? " have their own copies of these events. Editing here won't update them — refresh their events before sending."
            : ' has its own copy of these events. Editing here won\'t update it — refresh the quote\'s events before sending.'
        )}
      </p>
    </div>
  )
}

// Column definition with conflict info
interface EventRowData {
  event: InquiryEvent
  index: number
  conflict: ConflictInfo
}

// Define columns as a function to pass context for actions
const createColumns = (
  onDeleteClick: (event: InquiryEvent) => void,
  canDelete: boolean
): ColumnDef<EventRowData>[] => [
  {
    id: 'conflict',
    header: () => <span className="sr-only">Status</span>,
    cell: ({ row }) => {
      const { conflict } = row.original
      if (!conflict.hasConflict) {
        return <div className="w-4" />
      }
      const tooltipText = [
        conflict.conflictingEvents.length > 0 && `${conflict.conflictingEvents.length} booked event${conflict.conflictingEvents.length > 1 ? 's' : ''}`,
        conflict.conflictingInquiries.length > 0 && `${conflict.conflictingInquiries.length} overlapping lead${conflict.conflictingInquiries.length > 1 ? 's' : ''}`,
      ].filter(Boolean).join(', ')

      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <AlertTriangle className="h-4 w-4 text-orange-500 dark:text-orange-400" />
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>Scheduling conflict: {tooltipText}</p>
          </TooltipContent>
        </Tooltip>
      )
    },
    size: 40,
  },
  {
    id: 'sequence',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Event #" />
    ),
    cell: ({ row }) => {
      return <div className="font-medium">{row.original.index + 1}</div>
    },
  },
  {
    accessorKey: 'eventDate',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      const eventDate = row.original.event.eventDate
      if (!eventDate) {
        return (
          <div className="text-sm text-muted-foreground">
            Not set
          </div>
        )
      }
      return (
        <div className="text-sm">
          {formatDateTime(eventDate, row.original.event.eventTimezone)}
        </div>
      )
    },
    sortingFn: (rowA, rowB) => {
      const dateA = rowA.original.event.eventDate
      const dateB = rowB.original.event.eventDate
      if (!dateA && !dateB) return 0
      if (!dateA) return 1
      if (!dateB) return -1

      const secondsA = typeof dateA.seconds === 'bigint' ? Number(dateA.seconds) : dateA.seconds
      const secondsB = typeof dateB.seconds === 'bigint' ? Number(dateB.seconds) : dateB.seconds
      return secondsA - secondsB
    },
  },
  {
    accessorKey: 'eventLength',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Duration" />
    ),
    cell: ({ row }) => {
      const eventLength = row.original.event.eventLength
      if (!eventLength) {
        return (
          <div className="text-sm text-muted-foreground">
            -
          </div>
        )
      }
      const hours = eventLength % 1 === 0 ? eventLength : eventLength.toFixed(1)
      return <div className="text-sm">{hours} {eventLength === 1 ? 'hour' : 'hours'}</div>
    },
  },
  {
    accessorKey: 'eventGuests',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Guests" />
    ),
    cell: ({ row }) => {
      const eventGuests = row.original.event.eventGuests
      if (eventGuests === undefined || eventGuests === null || eventGuests === 0) {
        return (
          <div className="text-sm text-muted-foreground">
            -
          </div>
        )
      }
      return <div className="text-sm">{eventGuests}</div>
    },
  },
  {
    accessorKey: 'baristaCount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Baristas" />
    ),
    cell: ({ row }) => {
      // Show "-" for unset or zero counts — zero means "not configured"
      const baristaCount = row.original.event.baristaCount
      if (baristaCount == null || baristaCount === 0) {
        return (
          <div className="text-sm text-muted-foreground">
            -
          </div>
        )
      }
      return <div className="text-sm">{baristaCount}</div>
    },
  },
  {
    accessorKey: 'cartCount',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Carts" />
    ),
    cell: ({ row }) => {
      // Show "-" for unset or zero counts — zero means "not configured"
      const cartCount = row.original.event.cartCount
      if (cartCount == null || cartCount === 0) {
        return (
          <div className="text-sm text-muted-foreground">
            -
          </div>
        )
      }
      return <div className="text-sm">{cartCount}</div>
    },
  },
  {
    accessorKey: 'eventType',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Type" />
    ),
    cell: ({ row }) => {
      const eventType = row.original.event.eventTypeKey
      return (
        <EventTypeLabel
          type={eventType}
          className={eventType ? 'text-sm' : 'text-sm text-muted-foreground'}
        />
      )
    },
  },
  {
    accessorKey: 'eventLocation',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Location" />
    ),
    cell: ({ row }) => {
      const eventLocation = row.original.event.eventLocation
      if (!eventLocation) {
        return (
          <div className="text-sm text-muted-foreground">
            -
          </div>
        )
      }
      return <div className="text-sm truncate max-w-[200px]" title={eventLocation}>{eventLocation}</div>
    },
  },
  {
    id: 'actions',
    header: () => <span className="sr-only">Actions</span>,
    cell: ({ row }) => {
      return (
        <div className="flex justify-end">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 text-muted-foreground hover:text-destructive"
            disabled={!canDelete}
            title={canDelete ? 'Delete event' : 'Cannot delete the last event'}
            onClick={(e) => {
              e.stopPropagation()
              onDeleteClick(row.original.event)
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="sr-only">Delete</span>
          </Button>
        </div>
      )
    },
  },
]

export function LeadEventsCard({
  inquiryId,
  events,
  inquiryEventType,
  inquiryEventLocation,
  overlappingInquiries = [],
  overlappingEvents = [],
  quotes = [],
}: LeadEventsCardProps) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [editingEvent, setEditingEvent] = useState<{ event: InquiryEvent; index: number } | null>(null)
  const [createSheetOpen, setCreateSheetOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [eventToDelete, setEventToDelete] = useState<InquiryEvent | null>(null)

  const { deleteEvent, isLoading: isDeleting } = useDeleteInquiryEvent(inquiryId)

  // Cannot delete if only one event remains
  const canDelete = events.length > 1

  // Compute conflict info for each event
  const rowData: EventRowData[] = useMemo(() => {
    return events.map((event, index) => ({
      event,
      index,
      conflict: getEventConflicts(event, overlappingInquiries, overlappingEvents),
    }))
  }, [events, overlappingInquiries, overlappingEvents])

  // Check if any events have conflicts
  const hasAnyConflicts = rowData.some((r) => r.conflict.hasConflict)

  // Compute header stats
  const stats = useMemo(() => {
    const totalHours = events.reduce((sum, e) => sum + (e.eventLength || 0), 0)

    // Find date range
    const datesWithEvents = events
      .filter((e) => e.eventDate)
      .map((e) => {
        const seconds = typeof e.eventDate!.seconds === 'bigint'
          ? Number(e.eventDate!.seconds)
          : e.eventDate!.seconds
        return new Date(seconds * 1000)
      })
      .sort((a, b) => a.getTime() - b.getTime())

    const firstDate = datesWithEvents[0]
    const lastDate = datesWithEvents[datesWithEvents.length - 1]

    // Format date range
    let dateRange: string | null = null
    if (firstDate && lastDate) {
      const firstStr = format(firstDate, 'MMM d')
      const lastStr = format(lastDate, 'MMM d, yyyy')
      if (firstDate.getTime() === lastDate.getTime()) {
        dateRange = lastStr
      } else {
        dateRange = `${firstStr} – ${lastStr}`
      }
    }

    return {
      count: events.length,
      totalHours,
      dateRange,
    }
  }, [events])

  const handleDeleteClick = (event: InquiryEvent) => {
    setEventToDelete(event)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return

    try {
      await deleteEvent(eventToDelete.id)
      toast.success('Event deleted')
      setDeleteDialogOpen(false)
      setEventToDelete(null)
    } catch (error) {
      console.error('Failed to delete inquiry event:', error)
      reportError(error, { feature: 'inquiry_event_delete', kind: 'mutation' })
      toast.error('Failed to delete event', {
        description: getErrorDescription(error),
      })
    }
  }

  const columns = createColumns(handleDeleteClick, canDelete)

  const table = useReactTable({
    data: rowData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  })

  // Build description string
  const descriptionParts: string[] = []
  if (stats.count > 0) {
    descriptionParts.push(`${stats.count} event${stats.count !== 1 ? 's' : ''}`)
  }
  if (stats.totalHours > 0) {
    const hoursStr = stats.totalHours % 1 === 0 ? stats.totalHours : stats.totalHours.toFixed(1)
    descriptionParts.push(`${hoursStr} total hours`)
  }
  if (stats.dateRange) {
    descriptionParts.push(stats.dateRange)
  }
  const description = descriptionParts.join(' · ') || 'No events scheduled'

  // Get the first event's values to use as defaults for new events
  const firstEvent = events[0]

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CardTitle>Events</CardTitle>
            {hasAnyConflicts && (
              <Badge variant="secondary" className="bg-orange-50 text-orange-700 dark:bg-orange-950/50 dark:text-orange-300">
                Conflicts
              </Badge>
            )}
          </div>
          <Button onClick={() => setCreateSheetOpen(true)} size="sm">
            <Plus className="mr-2 h-4 w-4" />
            Add Event
          </Button>
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <QuoteEventsAdvisory quotes={quotes} />
        {events.length === 0 ? (
          <Empty className="border border-dashed py-12">
            <EmptyMedia variant="icon">
              <Calendar className="h-5 w-5" />
            </EmptyMedia>
            <EmptyDescription>No events yet</EmptyDescription>
          </Empty>
        ) : (
          <div className="rounded-lg border bg-card">
            <TableUI>
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableHead key={header.id}>
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map((row) => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => setEditingEvent({ event: row.original.event, index: row.original.index })}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-24 text-center"
                    >
                      No results.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </TableUI>
          </div>
        )}

        {/* Create Event Sheet */}
        <InquiryEventCreateSheet
          inquiryId={inquiryId}
          open={createSheetOpen}
          onOpenChange={setCreateSheetOpen}
          defaultEventLength={firstEvent?.eventLength ?? 0.5}
          defaultEventGuests={firstEvent?.eventGuests ?? 0}
          defaultBaristaCount={firstEvent?.baristaCount || 1}
          defaultCartCount={firstEvent?.cartCount || 1}
          defaultEventTime={formatTimestampToTimeString(firstEvent?.eventDate) || '12:00'}
          defaultEventType={firstEvent?.eventTypeKey ?? inquiryEventType}
          defaultEventLocation={firstEvent?.eventLocation ?? inquiryEventLocation}
          defaultEventTimezone={firstEvent?.eventTimezone}
        />

        {/* Edit Event Sheet */}
        {editingEvent && (
          <InquiryEventEditSheet
            inquiryId={inquiryId}
            event={editingEvent.event}
            eventIndex={editingEvent.index}
            open={!!editingEvent}
            onOpenChange={(open) => {
              if (!open) setEditingEvent(null)
            }}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                Delete Event
              </AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this event? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteConfirm}
                disabled={isDeleting}
                variant="destructive"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  )
}
